import {
    addActivePlugin$,
    addLexicalNode$,
    addImportVisitor$,
    addExportVisitor$,
    addComposerChild$,
    realmPlugin,
    useCellValue,
    rootEditor$,
} from "@mdxeditor/editor";
import { CitationNode, $createCitationNode, CitationPayload } from "./CitationNode";
import { decodeCitedText } from "./streaming";
import { $createTextNode, TextNode, $getRoot, $isTextNode, LexicalNode, $isElementNode } from "lexical";
import { useEffect } from "react";

// Global dictionary to store cited text by reference ID (used by legacy [cite:...] paths)
const citedTextDictionary: Map<string, string> = new Map();

// Primary citation syntax: ${A:artifact_id, start_line, end_line, "cited_text"}
//                          ${E:event_id,    start_line, end_line, "cited_text"}
//                          ${D:document_id, start_line, end_line, "cited_text"}
//                          ${H:conversation_id, start_epoch, end_epoch, "cited_text"}
// Content group: any non-special char, any backslash-escaped char, or " not followed by }
// (the closing token is always "}, so an inner " is safe as long as the next char is not })
// eslint-disable-next-line no-useless-escape
const CITATION_REGEX = /\$\{(A|E|D|H):([0-9]+),\s*([0-9]+),\s*([0-9]+),\s*"((?:[^"\\}]|\\.|"(?!}))*)"\}/g;

// Link syntax: a reference with NO cited_text — a pure pointer to an object.
//   ${A:1}  ${E:2}  ${D:3}  ${A:1, 34, 50}  ${E:4, 10, 30}
// Line numbers are optional. This deliberately does NOT match full citations
// (which have `, "text"` before the closing brace).
// eslint-disable-next-line no-useless-escape
const FOOTNOTE_REGEX = /\$\{(A|E|D):([0-9]+)(?:,\s*([0-9]+),\s*([0-9]+))?\}/g;

// LEGACY: [cite:...] format, still generated by old paths in chatMessage.tsx.
// TODO: Remove once all callers in chatMessage.tsx are updated.
const LEGACY_CITATION_REGEX = /\[cite:([^\]]+)\]/g;

// Function to store cited text and return a reference ID — used by legacy [cite:...] callers.
export function storeCitedText(citedText: string): string {
    const refId = `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    citedTextDictionary.set(refId, citedText);
    return refId;
}

// Function to retrieve cited text by reference ID — used by legacy [cite:...] parsing.
export function getCitedText(refId: string): string {
    return citedTextDictionary.get(refId) || "";
}

type CitationMatchResult = { fullMatch: string; matchIndex: number; payload: CitationPayload };

/** Parse a primary ${A:...} / ${E:...} / ${D:...} / ${H:...} regex match into a CitationPayload. */
function parseCitationMatch(match: RegExpMatchArray): CitationPayload | null {
    const [, type, id_str, start_str, end_str, cited_text_raw] = match;
    const cited_text = decodeCitedText(cited_text_raw);
    if (type === 'A') {
        return { citationType: "artifact", artifact_id: parseInt(id_str, 10), start_line: parseInt(start_str, 10), end_line: parseInt(end_str, 10), cited_text };
    } else if (type === 'E') {
        return { citationType: "event", event_id: parseInt(id_str, 10), start_line: parseInt(start_str, 10), end_line: parseInt(end_str, 10), cited_text };
    } else if (type === 'D') {
        return { citationType: "document", document_id: parseInt(id_str, 10), start_line: parseInt(start_str, 10), end_line: parseInt(end_str, 10), cited_text };
    } else if (type === 'H') {
        return { citationType: "history", conversation_id: parseInt(id_str, 10), start_time: parseInt(start_str, 10), end_time: parseInt(end_str, 10), cited_text };
    }
    return null;
}

/** Parse a link ${A:id} / ${A:id, s, e} regex match into a CitationPayload. */
function parseLinkMatch(match: RegExpMatchArray): CitationPayload | null {
    const [, type, id_str, start_str, end_str] = match;
    const start_line = start_str !== undefined ? parseInt(start_str, 10) : undefined;
    const end_line = end_str !== undefined ? parseInt(end_str, 10) : undefined;
    if (type === 'A') {
        return { citationType: "artifact", artifact_id: parseInt(id_str, 10), start_line, end_line, cited_text: "", isLink: true };
    } else if (type === 'E') {
        return { citationType: "event", event_id: parseInt(id_str, 10), start_line, end_line, cited_text: "", isLink: true };
    } else if (type === 'D') {
        return { citationType: "document", document_id: parseInt(id_str, 10), start_line, end_line, cited_text: "", isLink: true };
    }
    return null;
}

// LEGACY: parseCitationString — parses the inner content of a [cite:...] string.
// TODO: Remove when LEGACY_CITATION_REGEX is removed.
function parseCitationString(content: string): CitationPayload | null {
    const parts = content.split(':');

    if (parts[0] === 'artifact') {
        // LEGACY: [cite:artifact:ARTIFACT_ID:START_LINE:END_LINE:REF]
        if (parts.length < 5) return null;

        const artifact_id = parseInt(parts[1], 10);
        const start_line = parseInt(parts[2], 10);
        const end_line = parseInt(parts[3], 10);
        const cited_text_ref = parts[4];
        const cited_text = getCitedText(cited_text_ref);

        return {
            citationType: "artifact",
            artifact_id,
            start_line,
            end_line,
            cited_text
        };
    } else if (parts[0] === 'event') {
        // LEGACY: [cite:event:EVENT_ID:START_LINE:END_LINE:REF]
        if (parts.length < 5) return null;

        const event_id = parseInt(parts[1], 10);
        const start_line = parseInt(parts[2], 10);
        const end_line = parseInt(parts[3], 10);
        const cited_text_ref = parts[4];
        const cited_text = getCitedText(cited_text_ref);

        return {
            citationType: "event",
            event_id,
            start_line,
            end_line,
            cited_text
        };
    } else {
        // LEGACY: [cite:tool_use_id:document_id:document_name:document_type:cited_text_ref]
        if (parts.length < 5) return null;

        const tool_use_id = parts[0];
        const document_id = parseInt(parts[1], 10);
        const document_name = parts[2];
        const document_type = parts[3];
        const cited_text_ref = parts[4];
        const cited_text = getCitedText(cited_text_ref);

        return {
            citationType: "tool",
            tool_use_id,
            document: {
                type: document_type,
                id: document_id,
                name: document_name,
                tool_use_id: tool_use_id || undefined,
            },
            cited_text
        };
    }
}

/** Extract all citation matches (primary ${} and legacy [cite:...]) from text, sorted by position. */
function extractCitationMatches(text: string): CitationMatchResult[] {
    const allMatches: CitationMatchResult[] = [];
    let match;

    CITATION_REGEX.lastIndex = 0;
    while ((match = CITATION_REGEX.exec(text)) !== null) {
        const payload = parseCitationMatch(match);
        if (payload) allMatches.push({ fullMatch: match[0], matchIndex: match.index!, payload });
    }

    // Link matches (${A:id} / ${A:id, s, e}) — references with no cited_text.
    FOOTNOTE_REGEX.lastIndex = 0;
    while ((match = FOOTNOTE_REGEX.exec(text)) !== null) {
        const payload = parseLinkMatch(match);
        if (payload) allMatches.push({ fullMatch: match[0], matchIndex: match.index!, payload });
    }

    // LEGACY: also extract [cite:...] matches.
    // TODO: Remove when LEGACY_CITATION_REGEX is removed.
    LEGACY_CITATION_REGEX.lastIndex = 0;
    while ((match = LEGACY_CITATION_REGEX.exec(text)) !== null) {
        const payload = parseCitationString(match[1]);
        if (payload) allMatches.push({ fullMatch: match[0], matchIndex: match.index!, payload });
    }

    return allMatches.sort((a, b) => a.matchIndex - b.matchIndex);
}

/** Returns true if the text contains any recognized citation syntax. */
function hasCitationSyntax(text: string): boolean {
    CITATION_REGEX.lastIndex = 0;
    if (CITATION_REGEX.test(text)) return true;
    FOOTNOTE_REGEX.lastIndex = 0;
    if (FOOTNOTE_REGEX.test(text)) return true;
    // LEGACY
    LEGACY_CITATION_REGEX.lastIndex = 0;
    return LEGACY_CITATION_REGEX.test(text);
}

/**
 * Returns true if the text contains a complete citation OR the start of one
 * that may be split across nodes (e.g. `${A:1,1,1,"partial` without closing `"}`).
 * Used by the update listener so split citations still trigger the merge+process pass.
 */
function hasCitationOrPartialSyntax(text: string): boolean {
    if (hasCitationSyntax(text)) return true;
    // Partial primary: ${A: or ${E: anywhere, possibly without closing }
    if (/\$\{(A|E|D|H):/.test(text)) return true;
    // LEGACY partial
    if (/\[cite:/.test(text)) return true;

    return false;
}

function serializeCitationPayload(payload: CitationPayload): string {
    if (payload.citationType === 'artifact') {
        // Link: no cited_text — round-trip to ${A:id} or ${A:id, s, e}.
        if (payload.isLink || !payload.cited_text) {
            if (payload.start_line !== undefined && payload.end_line !== undefined) {
                return `\${A:${payload.artifact_id}, ${payload.start_line}, ${payload.end_line}}`;
            }
            return `\${A:${payload.artifact_id}}`;
        }
        // eslint-disable-next-line no-useless-escape
        const cited_text = (payload.cited_text || "").replace(/"/g, '\\"');
        return `\${A:${payload.artifact_id}, ${payload.start_line}, ${payload.end_line}, "${cited_text}"}`;
    } else if (payload.citationType === 'event') {
        // Link: no cited_text — round-trip to ${E:id} or ${E:id, s, e}.
        if (payload.isLink || !payload.cited_text) {
            if (payload.start_line !== undefined && payload.end_line !== undefined) {
                return `\${E:${payload.event_id}, ${payload.start_line}, ${payload.end_line}}`;
            }
            return `\${E:${payload.event_id}}`;
        }
        // eslint-disable-next-line no-useless-escape
        const cited_text = (payload.cited_text || "").replace(/"/g, '\\"');
        return `\${E:${payload.event_id}, ${payload.start_line}, ${payload.end_line}, "${cited_text}"}`;
    } else if (payload.citationType === 'document') {
        // Link: no cited_text — round-trip to ${D:id} or ${D:id, s, e}.
        if (payload.isLink || !payload.cited_text) {
            if (payload.start_line !== undefined && payload.end_line !== undefined) {
                return `\${D:${payload.document_id}, ${payload.start_line}, ${payload.end_line}}`;
            }
            return `\${D:${payload.document_id}}`;
        }
        // eslint-disable-next-line no-useless-escape
        const cited_text = (payload.cited_text || "").replace(/"/g, '\\"');
        return `\${D:${payload.document_id}, ${payload.start_line}, ${payload.end_line}, "${cited_text}"}`;
    } else if (payload.citationType === 'history') {
        // eslint-disable-next-line no-useless-escape
        const cited_text = (payload.cited_text || "").replace(/"/g, '\\"');
        return `\${H:${payload.conversation_id}, ${payload.start_time}, ${payload.end_time}, "${cited_text}"}`;
    } else {
        // LEGACY: tool citations serialise back to [cite:...] format.
        // TODO: Remove when tool citations are no longer in use.
        const citedTextRef = storeCitedText(payload.cited_text || "");
        return `[cite:${payload.tool_use_id || ""}:${payload.document.id}:${payload.document.name}:${payload.document.type}:${citedTextRef}]`;
    }
}

function processCitationsInText(textNode: TextNode): void {
    const text = textNode.getTextContent();
    const citations = extractCitationMatches(text);

    if (citations.length === 0) {
        return;
    }

    const nodes: LexicalNode[] = [];
    let lastIndex = 0;

    citations.forEach(({ fullMatch, matchIndex, payload }) => {
        // Add text before citation if any
        if (matchIndex > lastIndex) {
            const textBefore = text.slice(lastIndex, matchIndex);
            if (textBefore) {
                nodes.push($createTextNode(textBefore));
            }
        }

        // Create citation node
        nodes.push($createCitationNode(payload));

        lastIndex = matchIndex + fullMatch.length;
    });

    // Add remaining text after last citation
    if (lastIndex < text.length) {
        const textAfter = text.slice(lastIndex);
        if (textAfter) {
            nodes.push($createTextNode(textAfter));
        }
    }

    // Replace the text node with the new nodes
    if (nodes.length > 0) {
        try {
            nodes.forEach((node, index) => {
                if (index === 0) {
                    textNode.replace(node);
                } else {
                    nodes[index - 1].insertAfter(node);
                }
            });
        } catch (error) {
            console.error("Error replacing text node:", error);
        }
    }
}

function getAllTextNodes(node: LexicalNode): TextNode[] {
    const textNodes: TextNode[] = [];

    if ($isTextNode(node)) {
        textNodes.push(node);
    }

    if ($isElementNode(node)) {
        const children = node.getChildren();
        children.forEach((child: LexicalNode) => {
            textNodes.push(...getAllTextNodes(child));
        });
    }

    return textNodes;
}

function getAllCitationNodes(node: LexicalNode): any[] {
    const citationNodes: any[] = [];

    if (node.getType() === 'citation') {
        citationNodes.push(node);
    }

    if ($isElementNode(node)) {
        const children = node.getChildren();
        children.forEach(child => {
            citationNodes.push(...getAllCitationNodes(child));
        });
    }

    return citationNodes;
}

function findAndMergeCitationTextNodes(root: LexicalNode): void {
    const textNodes = getAllTextNodes(root);

    for (let i = 0; i < textNodes.length; i++) {
        const startNode = textNodes[i];
        const startText = startNode.getTextContent();

        // Skip nodes that already contain a complete citation or have no citation syntax at all
        if (hasCitationSyntax(startText) || !hasCitationOrPartialSyntax(startText)) continue;

        // Greedily collect subsequent text nodes (regardless of type or parent) until the
        // citation syntax is complete. This handles bold/italic splits (3+ nodes) and
        // cross-paragraph splits alike, without caring about the tree shape in between.
        let combinedText = startText;
        let endIndex = -1;

        for (let j = i + 1; j < textNodes.length; j++) {
            combinedText += textNodes[j].getTextContent();
            if (hasCitationSyntax(combinedText)) {
                endIndex = j;
                break;
            }
            // Give up if the partial marker has completely disappeared
            if (!hasCitationOrPartialSyntax(combinedText)) break;
        }

        if (endIndex === -1) continue;

        const endNode = textNodes[endIndex];
        const startParent = startNode.getParent();
        const endParent = endNode.getParent();

        try {
            if (startParent?.getKey() === endParent?.getKey()) {
                // Same parent — remove every sibling between startNode and endNode (inclusive).
                // This covers plain text, bold, italic, linebreak, or any other inline node
                // that the editor may have inserted inside the citation span.
                const nodesToRemove: LexicalNode[] = [];
                let cursor: LexicalNode | null = startNode.getNextSibling();
                while (cursor && cursor.getKey() !== endNode.getKey()) {
                    nodesToRemove.push(cursor);
                    cursor = cursor.getNextSibling();
                }
                nodesToRemove.push(endNode);

                startNode.setTextContent(combinedText);
                nodesToRemove.forEach(n => n.remove());
            } else {
                // Cross-paragraph — rebuild the merged text inserting \n at paragraph
                // boundaries, then remove the consumed nodes and any empty paragraphs.
                let mergedText = startText;
                let prevParentKey = startParent?.getKey();

                for (let j = i + 1; j <= endIndex; j++) {
                    const node = textNodes[j];
                    const nodeParentKey = node.getParent()?.getKey();
                    if (nodeParentKey !== prevParentKey) {
                        mergedText += '\n';
                        prevParentKey = nodeParentKey;
                    }
                    mergedText += node.getTextContent();
                }

                startNode.setTextContent(mergedText);

                for (let j = i + 1; j <= endIndex; j++) {
                    const node = textNodes[j];
                    const parent = node.getParent();
                    node.remove();
                    if (parent && $isElementNode(parent) && parent.getChildrenSize() === 0) {
                        parent.remove();
                    }
                }
            }

            // Reflect the merge in our working array and recheck position i
            textNodes.splice(i + 1, endIndex - i);
            i--;
        } catch (error) {
            console.error("Error merging citation text nodes:", error);
        }
    }
}

function CitationProcessor() {
    const editor = useCellValue(rootEditor$);

    useEffect(() => {

        if (!editor) return;

        const processTree = () => {

            editor.update(() => {

                const root = $getRoot();

                // First, try to merge any split citation text nodes
                findAndMergeCitationTextNodes(root);

                // We need to process citations multiple times because text nodes can be split
                // Keep processing until no more citations are found
                let foundCitations = true;
                let iterations = 0;
                const maxIterations = 10; // Prevent infinite loops

                while (foundCitations && iterations < maxIterations) {
                    foundCitations = false;
                    iterations++;

                    const textNodes = getAllTextNodes(root);

                    // Process each text node for citations
                    textNodes.forEach(textNode => {
                        const text = textNode.getTextContent();
                        if (hasCitationSyntax(text)) {
                            processCitationsInText(textNode);
                            foundCitations = true;
                        }
                    });
                }

                if (iterations >= maxIterations) {
                    console.warn("Citation processing reached maximum iterations");
                }
            });
        };

        const forceCitationRerender = () => {
            editor.update(() => {
                const root = $getRoot();
                const citationNodes = getAllCitationNodes(root);

                // Force re-render by marking citation nodes as dirty
                citationNodes.forEach((citationNode: any) => {
                    if (citationNode && typeof citationNode.markDirty === 'function') {
                        citationNode.markDirty();
                    }
                });
            });
        };

        // Process on initial load and when content changes
        const timeoutId = setTimeout(processTree, 100);

        // Listen for when the editor becomes visible again (intersection observer)
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        editor.getEditorState().read(() => {
                            const root = $getRoot();
                            const citationNodes = getAllCitationNodes(root);
                            const textNodes = getAllTextNodes(root);
                            const hasCitations = textNodes.some(node => hasCitationSyntax(node.getTextContent()));

                            // If we have citation nodes but no raw citations, force re-render
                            if (citationNodes.length > 0 && !hasCitations) {
                                forceCitationRerender();
                            }
                        });
                    }, 100);
                }
            });
        });

        const editorElement = editor.getRootElement();
        if (editorElement) {
            observer.observe(editorElement);
        }

        // Also listen for editor updates and process citations. Processed citations no longer
        // show their syntax in the root text, so only a complete raw citation (possibly split
        // across text nodes) triggers a pass; an unfinished one at the end of a stream does not.
        const removeUpdateListener = editor.registerUpdateListener(() => {
            setTimeout(() => {
                const hasRawCitation = editor.getEditorState().read(() => hasCitationSyntax($getRoot().getTextContent()));
                if (hasRawCitation) setTimeout(processTree, 50);
            }, 100);
        });

        return () => {
            clearTimeout(timeoutId);
            removeUpdateListener();
            observer.disconnect();
        };
    }, [editor]);

    return null;
}

export const citationsPlugin = realmPlugin<{}>({
    init(realm, params) {
        realm.pubIn({
            [addActivePlugin$]: "citations",
            [addLexicalNode$]: CitationNode,
            [addComposerChild$]: CitationProcessor,
            [addImportVisitor$]: {
                // Core's text visitor has the default priority and is registered first, so without
                // this it claims every text node and citations only appear after CitationProcessor's
                // delayed pass, showing raw syntax in between.
                priority: 100,
                testNode: (mdastNode: any) => {
                    return mdastNode.type === 'text' && hasCitationSyntax(mdastNode.value || '');
                },
                visitNode: ({ mdastNode, actions }: any) => {

                    if (mdastNode.type === 'text') {
                        const text = mdastNode.value || '';
                        const format = actions.getParentFormatting();
                        const style = actions.getParentStyle();
                        const $createFormattedTextNode = (value: string) => {
                            const node = $createTextNode(value).setFormat(format);
                            return style ? node.setStyle(style) : node;
                        };

                        const citations = extractCitationMatches(text);

                        if (citations.length > 0) {
                            let lastIndex = 0;

                            citations.forEach(({ fullMatch, matchIndex, payload }) => {
                                // Add text before citation
                                if (matchIndex > lastIndex) {
                                    const textBefore = text.slice(lastIndex, matchIndex);
                                    if (textBefore) {
                                        const textNode = $createFormattedTextNode(textBefore);
                                        actions.addAndStepInto(textNode);
                                    }
                                }

                                // Add citation node
                                const citationNode = $createCitationNode(payload);
                                actions.addAndStepInto(citationNode);

                                lastIndex = matchIndex + fullMatch.length;
                            });

                            // Add remaining text
                            if (lastIndex < text.length) {
                                const textAfter = text.slice(lastIndex);
                                if (textAfter) {
                                    const textNode = $createFormattedTextNode(textAfter);
                                    actions.addAndStepInto(textNode);
                                }
                            }
                        } else {
                            // No citations found, just add the text as is
                            const textNode = $createFormattedTextNode(text);
                            actions.addAndStepInto(textNode);
                        }
                    }
                }
            },
            [addExportVisitor$]: {
                testLexicalNode: (lexicalNode: LexicalNode) => {

                    return lexicalNode instanceof CitationNode;
                },
                visitLexicalNode: ({ lexicalNode, actions }: any) => {

                    if (lexicalNode instanceof CitationNode) {
                        const citationData = lexicalNode.getCitationData();
                        const citationText = serializeCitationPayload(citationData);
                        actions.addAndStepInto("text", { value: citationText }, false);
                    }
                }
            },
        });
    },
    update(realm, params) { },
});
